import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Tabs, Tab,
  TextField, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  fetchCustomerType,
  updateCustomerType,
  deleteCustomerType,
  addCustomerCriteria,
  deleteCustomerCriteria,
  addCustomerCriteriaValue,
  deleteCustomerCriteriaValue,
} from "../../api/companyApi";
import { wrapTextCell } from "../../utils/gridUtils";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";
import KpiConstraintAlert from "../../components/common/KpiConstraintAlert";
import KpiDetailDialog from "../../components/common/KpiDetailDialog";
import { useKpiUsageStatus } from "../../hooks/useKpiUsageStatus";

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function CustomerTypeDetailPage() {
  const { businessDomainId, customerTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // KPI state
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditMode, setKpiEditMode] = useState(false);
  const [kpiEditIndex, setKpiEditIndex] = useState(-1);
  const [kpiForm, setKpiForm] = useState({
    indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "",
  });
  const [kpiConfirmOpen, setKpiConfirmOpen] = useState(false);
  const [kpiDeleteIndex, setKpiDeleteIndex] = useState(-1);
  const [kpiDetailOpen, setKpiDetailOpen] = useState(false);
  const [kpiDetailRow, setKpiDetailRow] = useState(null);

  // Criteria state
  const [criteriaDialogOpen, setCriteriaDialogOpen] = useState(false);
  const [criteriaForm, setCriteriaForm] = useState({
    classificationCriteriaCd: "", classificationCriteriaName: "",
  });
  const [criteriaConfirmOpen, setCriteriaConfirmOpen] = useState(false);
  const [criteriaDeleteTarget, setCriteriaDeleteTarget] = useState(null);

  // Criteria Value state
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [cvDialogOpen, setCvDialogOpen] = useState(false);
  const [cvForm, setCvForm] = useState({
    classificationCriteriaValueCd: "", classificationCriteriaValueName: "",
  });
  const [cvConfirmOpen, setCvConfirmOpen] = useState(false);
  const [cvDeleteTarget, setCvDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setData(await fetchCustomerType(customerTypeId));
    } catch (e) {
      notify.apiError(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerTypeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Delete customer type ──
  const handleDeleteCustomerType = async () => {
    try {
      await deleteCustomerType(customerTypeId);
      notify.success(t("message.delete.success"));
      setConfirmOpen(false);
      navigate(`/design/${businessDomainId}/customer-types`, { replace: true });
    } catch (e) {
      notify.apiError(e);
    }
  };

  // ── KPI CRUD (via indicators array on customerType) ──
  const openKpiAddDialog = () => {
    setKpiEditMode(false); setKpiEditIndex(-1);
    setKpiForm({ indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" });
    setKpiDialogOpen(true);
  };
  const openKpiEditDialog = (index, indicator) => {
    setKpiEditMode(true); setKpiEditIndex(index);
    setKpiForm({
      indicatorName: indicator.indicatorName || "", unit: indicator.unit || "",
      measuringMethod: indicator.measuringMethod || "", evaluationStandard: indicator.evaluationStandard || "",
      description: indicator.description || "",
    });
    setKpiDialogOpen(true);
  };
  const handleKpiSave = async () => {
    if (!kpiForm.indicatorName || !kpiForm.unit || !kpiForm.description) {
      notify.error(t("message.error.required")); return;
    }
    try {
      const indicators = [...(data.indicators || [])];
      if (kpiEditMode) {
        indicators[kpiEditIndex] = { ...indicators[kpiEditIndex], ...kpiForm, updatedAt: new Date().toISOString() };
      } else {
        if (indicators.some((ind) => ind.indicatorName === kpiForm.indicatorName)) {
          notify.error(t("customerKpi.error.duplicate")); return;
        }
        indicators.push({ ...kpiForm, createdAt: new Date().toISOString() });
      }
      await updateCustomerType({ ...data, indicators });
      notify.success(kpiEditMode ? t("message.update.success") : t("message.create.success"));
      setKpiDialogOpen(false);
      loadData();
    } catch (e) {
      notify.apiError(e);
    }
  };
  const handleKpiDelete = async () => {
    try {
      const indicators = [...(data.indicators || [])];
      indicators.splice(kpiDeleteIndex, 1);
      await updateCustomerType({ ...data, indicators });
      notify.success(t("message.delete.success"));
      setKpiConfirmOpen(false); setKpiDeleteIndex(-1);
      loadData();
    } catch (e) {
      notify.apiError(e);
    }
  };

  // ── Criteria CRUD (separate API) ──
  const openCriteriaAddDialog = () => {
    setCriteriaForm({ classificationCriteriaCd: "", classificationCriteriaName: "" });
    setCriteriaDialogOpen(true);
  };
  const handleCriteriaSave = async () => {
    if (!criteriaForm.classificationCriteriaCd || !criteriaForm.classificationCriteriaName) {
      notify.error(t("message.error.required")); return;
    }
    try {
      await addCustomerCriteria(customerTypeId, { ...criteriaForm, createdAt: new Date().toISOString() });
      notify.success(t("message.create.success"));
      setCriteriaDialogOpen(false);
      loadData();
    } catch (e) {
      notify.apiError(e);
    }
  };
  const handleCriteriaDelete = async () => {
    try {
      await deleteCustomerCriteria(customerTypeId, criteriaDeleteTarget);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) {
      notify.apiError(e);
    }
    setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null);
  };

  // ── Criteria Value CRUD ──
  const openCvAddDialog = () => {
    setCvForm({ classificationCriteriaValueCd: "", classificationCriteriaValueName: "" });
    setCvDialogOpen(true);
  };
  const handleCvSave = async () => {
    if (!cvForm.classificationCriteriaValueCd || !cvForm.classificationCriteriaValueName) {
      notify.error(t("message.error.required")); return;
    }
    try {
      await addCustomerCriteriaValue(customerTypeId, {
        classificationCriteria: selectedCriteria,
        classificationCriteriaValueCd: cvForm.classificationCriteriaValueCd,
        classificationCriteriaValueName: cvForm.classificationCriteriaValueName,
        createdValueAt: new Date().toISOString(),
      });
      notify.success(t("message.create.success"));
      setCvDialogOpen(false);
      loadData();
    } catch (e) {
      notify.apiError(e);
    }
  };
  const handleCvDelete = async () => {
    try {
      await deleteCustomerCriteriaValue(customerTypeId, cvDeleteTarget);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) {
      notify.apiError(e);
    }
    setCvConfirmOpen(false); setCvDeleteTarget(null);
  };

  const { usedKpiNames } = useKpiUsageStatus(businessDomainId);

  if (!data) return null;

  const personalityLabel = data.personality === "Person"
    ? t("customerType.personality.person")
    : data.personality === "Company"
      ? t("customerType.personality.company")
      : data.personality || "";

  const indicators = data.indicators || [];
  const criterias = data.classificationCriterias || [];
  const hasLockedKpi = indicators.some((ind) => usedKpiNames.has(ind.indicatorName));

  // Filter criteria values for the selected criteria
  const criteriaValues = selectedCriteria
    ? (data.classificationCriteriaValues || []).filter(
        (v) => v.classificationCriteria?.classificationCriteriaName === selectedCriteria.classificationCriteriaName
      )
    : [];

  // KPI columns
  const kpiColumns = [
    {
      field: "indicatorName", headerName: t("customerKpi.indicatorName"), flex: 1, minWidth: 150,
      renderCell: (p) => {
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {p.row.indicatorName}
            {isLocked && (
              <Chip icon={<LockIcon fontSize="small" />} label={t("common.inUse")} size="small" color="warning" variant="outlined" />
            )}
          </Box>
        );
      },
    },
    { field: "unit", headerName: t("customerKpi.unit"), width: 120 },
    { field: "description", headerName: t("customerKpi.description"), flex: 1, minWidth: 150, renderCell: wrapTextCell },
    {
      field: "actions", headerName: t("common.actions"), width: 140, sortable: false,
      renderCell: (p) => {
        const idx = indicators.findIndex((ind) => ind.indicatorName === p.row.indicatorName);
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (
          <Box>
            <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}>
              <span>
                <IconButton size="small" disabled={isLocked}
                  onClick={(e) => { e.stopPropagation(); openKpiEditDialog(idx, p.row); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}>
              <span>
                <IconButton size="small" color="error" disabled={isLocked}
                  onClick={(e) => { e.stopPropagation(); setKpiDeleteIndex(idx); setKpiConfirmOpen(true); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Criteria columns
  const criteriaColumns = [
    { field: "classificationCriteriaCd", headerName: t("customerCriteria.code"), width: 160 },
    { field: "classificationCriteriaName", headerName: t("customerCriteria.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            setCriteriaDeleteTarget({
              classificationCriteriaCd: p.row.classificationCriteriaCd,
              classificationCriteriaName: p.row.classificationCriteriaName,
            });
            setCriteriaConfirmOpen(true);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Criteria Value columns
  const cvColumns = [
    { field: "classificationCriteriaValueCd", headerName: t("criteriaValue.code"), width: 160 },
    { field: "classificationCriteriaValueName", headerName: t("criteriaValue.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            setCvDeleteTarget(p.row);
            setCvConfirmOpen(true);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      {/* 案B：KPIが使用中の場合のInfoアラート */}
      {hasLockedKpi && <KpiConstraintAlert variant="type-locked" />}
      {/* Header with Edit + Delete buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("customerType.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`/design/${businessDomainId}/customer-types/${customerTypeId}/edit`)}>
            {t("common.edit")}
          </Button>
          {/* 案C：KPIが使用中の場合、削除ボタンをdisabled＋Tooltip */}
          <Tooltip title={hasLockedKpi ? t("kpi.constraint.type.locked") : ""}>
            <span>
              <Button variant="contained" color="error" startIcon={<DeleteIcon />}
                disabled={hasLockedKpi}
                onClick={() => setConfirmOpen(true)}>
                {t("common.delete")}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Basic info */}
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("customerType.name")} value={data.customerTypeName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("customerType.personality")} value={personalityLabel} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("customerType.values")} value={data.values} />
          </Grid>
        </Grid>
      </DetailSection>

      {/* Tabs for sub-entities */}
      <Box sx={{ mt: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabIndex} onChange={(_, v) => { setTabIndex(v); setSelectedCriteria(null); }}>
          <Tab label={t("customerKpi.title")} />
          <Tab label={t("customerCriteria.title")} />
        </Tabs>
      </Box>

      {/* Tab: Customer KPI */}
      <TabPanel value={tabIndex} index={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("customerKpi.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openKpiAddDialog}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={indicators} columns={kpiColumns} getRowId={(r) => r.indicatorName}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          onRowClick={(params) => { setKpiDetailRow(params.row); setKpiDetailOpen(true); }}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </TabPanel>

      {/* Tab: Customer Criteria */}
      <TabPanel value={tabIndex} index={1}>
        {selectedCriteria ? (
          /* ── Criteria Value view ── */
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton onClick={() => setSelectedCriteria(null)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle1">
                {selectedCriteria.classificationCriteriaName}：{t("criteriaValue.list.title")}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCvAddDialog}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid
              rows={criteriaValues}
              columns={cvColumns}
              getRowId={(r) => r.classificationCriteriaValueCd}
              autoHeight
              pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
            />
          </Box>
        ) : (
          /* ── Criteria list view ── */
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1">{t("customerCriteria.list.title")}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCriteriaAddDialog}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid
              rows={criterias} columns={criteriaColumns}
              getRowId={(r) => r.classificationCriteriaCd}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
              onRowClick={(params) => setSelectedCriteria(params.row)}
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        )}
      </TabPanel>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/customer-types`)}>
          {t("common.back")}
        </Button>
      </Box>

      {/* Delete customer type confirmation */}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDeleteCustomerType} onCancel={() => setConfirmOpen(false)} />

      {/* KPI Detail Dialog */}
      <KpiDetailDialog
        open={kpiDetailOpen}
        onClose={() => { setKpiDetailOpen(false); setKpiDetailRow(null); }}
        indicator={kpiDetailRow}
      />

      {/* KPI Add/Edit Dialog */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{kpiEditMode ? t("customerKpi.edit.title") : t("customerKpi.create.title")}</DialogTitle>
        <DialogContent>
          {/* 案D：KPI追加・編集時のInfo */}
          <KpiConstraintAlert
            variant={kpiEditMode
              ? (usedKpiNames.has(kpiForm.indicatorName) ? "edit-locked" : "edit-unused")
              : "create"}
            sx={{ mt: 1 }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("customerKpi.indicatorName")}
              value={kpiForm.indicatorName} onChange={(e) => setKpiForm({ ...kpiForm, indicatorName: e.target.value })} />
            <TextField fullWidth required multiline label={t("customerKpi.unit")}
              value={kpiForm.unit} onChange={(e) => setKpiForm({ ...kpiForm, unit: e.target.value })} />
            <TextField fullWidth multiline label={t("customerKpi.measuringMethod")}
              value={kpiForm.measuringMethod} onChange={(e) => setKpiForm({ ...kpiForm, measuringMethod: e.target.value })} />
            <TextField fullWidth multiline label={t("customerKpi.evaluationStandard")}
              value={kpiForm.evaluationStandard} onChange={(e) => setKpiForm({ ...kpiForm, evaluationStandard: e.target.value })} />
            <TextField fullWidth required multiline label={t("customerKpi.description")}
              value={kpiForm.description} onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleKpiSave}
            disabled={kpiEditMode && usedKpiNames.has(kpiForm.indicatorName)}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={kpiConfirmOpen} onConfirm={handleKpiDelete}
        onCancel={() => { setKpiConfirmOpen(false); setKpiDeleteIndex(-1); }} />

      {/* Criteria Add Dialog */}
      <Dialog open={criteriaDialogOpen} onClose={() => setCriteriaDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("customerCriteria.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("customerCriteria.code")}
              value={criteriaForm.classificationCriteriaCd}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("customerCriteria.name")}
              value={criteriaForm.classificationCriteriaName}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCriteriaDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCriteriaSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={criteriaConfirmOpen} onConfirm={handleCriteriaDelete}
        onCancel={() => { setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null); }} />

      {/* Criteria Value Add Dialog */}
      <Dialog open={cvDialogOpen} onClose={() => setCvDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("criteriaValue.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("criteriaValue.code")}
              value={cvForm.classificationCriteriaValueCd}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("criteriaValue.name")}
              value={cvForm.classificationCriteriaValueName}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCvDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCvSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={cvConfirmOpen} onConfirm={handleCvDelete}
        onCancel={() => { setCvConfirmOpen(false); setCvDeleteTarget(null); }} />
    </Box>
  );
}
